import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  Form,
  Select,
  Checkbox,
  Space,
  Tooltip,
  Input,
  Modal,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  getAllModules,
  getAllPermissions,
  ModuleRecord,
  PermissionRecord,
  PermissionUpdate,
  postPermission,
  updatePermission,
  deletePermission,
} from "../../../core/api/permissionMatrix";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import useGetEmployees from "../../../core/api/useGetEmployees";
import { useRoles } from "../../../core/api/useRoles";
import { useAuthToken } from "../../../core/data/redux/useAuthToken";
import "../../hrm/attendance/calendar.css";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { usePermission } from "../../../core/api/useRolePermission";
import Table from "../../../core/common/dataTable/index";

const { Option } = Select;

const PermissionPage: React.FC = () => {
  const location = useLocation();
  const [permissions, setPermissions] = useState<PermissionRecord[]>([]);
  const [modules, setModules] = useState<ModuleRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEditingPermissionId, setCurrentEditingPermissionId] = useState<
    number | null
  >(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletePermissionId, setDeletePermissionId] = useState<number | null>(
    null
  );
  const [form] = Form.useForm();
  const { roles } = useRoles();
  const { employees } = useGetEmployees();
  const empToken = useAuthToken();
  const [moduleSelectorVisible, setModuleSelectorVisible] = useState(false);
  const [selectedModules, setSelectedModules] = useState<number[]>([]);
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState("Default");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  // 🔹 New state for Instructions Modal
  const [instructionsVisible, setInstructionsVisible] = useState(false);

  // Permissions
  const canCreate = usePermission("Roles permissions", "create");
  const canUpdate = usePermission("Roles permissions", "write");
  const canDelete = usePermission("Roles permissions", "delete");

  const employeeNameMap = useMemo(() => {
    const map: Record<
      string,
      { name: string; department: string; role: string }
    > = {};
    if (Array.isArray(employees) && employees.length > 0) {
      employees.forEach((emp) => {
        map[emp.employee_id] = {
          name: `${emp.firstname} ${emp.lastname}`,
          department: emp.department_name || "Unknown Department",
          role: emp.role_name || "Unknown Role",
        };
      });
    }
    return map;
  }, [employees]);

  const filteredPermissions = useMemo(() => {
    let filtered = permissions;
    const filterRole = location.state?.filterRole;

    if (filterRole) {
      filtered = filtered.filter(
        (permission) => permission.role_name === filterRole
      );
    }

    if (searchText) {
      filtered = filtered.filter((permission) => {
        const emp = permission.employee_id
          ? employeeNameMap[permission.employee_id]
          : null;
        const empName = emp ? emp.name.toLowerCase() : "";
        const moduleName = permission.module?.name?.toLowerCase() || "";
        const roleName = permission.role_name?.toLowerCase() || "";
        const search = searchText.toLowerCase();
        return (
          empName.includes(search) ||
          moduleName.includes(search) ||
          roleName.includes(search)
        );
      });
    }

    if (selectedRole) {
      filtered = filtered.filter(
        (permission) => permission.role_name === selectedRole
      );
    }

    if (selectedModule) {
      filtered = filtered.filter(
        (permission) => permission.module?.name === selectedModule
      );
    }

    if (filterType === "Custom") {
      filtered = filtered.filter(
        (p) => p.employee_id && p.employee_id.trim() !== ""
      );
    }

    return filtered;
  }, [
    permissions,
    searchText,
    selectedRole,
    selectedModule,
    employeeNameMap,
    location.state,
    filterType,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [modulesData, permissionData] = await Promise.all([
          getAllModules(empToken),
          getAllPermissions(empToken),
        ]);
        setModules(Array.isArray(modulesData) ? modulesData : []);
        setPermissions(Array.isArray(permissionData) ? permissionData : []);
      } catch (error) {
        console.error("Error fetching permission data");
      } finally {
        setLoading(false);
      }
    };

    if (empToken) {
      fetchData();
    }
  }, [empToken]);

  const handleAddPermission = async (values: any) => {
    try {
      const normalizedEmployeeId = values.employee_id || null;
      const selectedModules: number[] = values.model_id || [];

      const duplicateFound = selectedModules.some((moduleId) =>
        permissions.some(
          (p) =>
            p.model_id === moduleId &&
            p.role_name === values.role_name &&
            (p.employee_id || null) === normalizedEmployeeId
        )
      );

      if (duplicateFound && !isEditMode) {
        toast.warning("One or more selected permissions already exist.");
        return;
      }

      if (isEditMode && currentEditingPermissionId !== null) {
        const updateData: PermissionUpdate = {
          permission_id: currentEditingPermissionId,
          allow_all: values.allow_all,
          read: values.read,
          write: values.write,
          create: values.create,
          delete: values.delete,
          export: values.export,
        };

        await updatePermission(updateData, empToken);
      } else {
        await Promise.all(
          selectedModules.map(async (moduleId) => {
            const payload = {
              model_id: moduleId,
              role_name: values.role_name,
              employee_id: normalizedEmployeeId,
              allow_all: values.allow_all,
              read: values.read,
              write: values.write,
              create: values.create,
              delete: values.delete,
              export: values.export,
            };
            await postPermission(payload, empToken);
          })
        );
      }

      setModalVisible(false);
      setIsEditMode(false);
      setCurrentEditingPermissionId(null);
      form.resetFields();
      setSelectedModules([]);
      const updated = await getAllPermissions(empToken);
      setPermissions(updated);
    } catch (error) {
      console.error("Failed to save permission");
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setIsEditMode(false);
    setCurrentEditingPermissionId(null);
    form.resetFields();
    setSelectedModules([]);
    setModuleSelectorVisible(false);
  };

  const handleCancelForm = () => {
    setIsEditMode(false);
    setCurrentEditingPermissionId(null);
    form.resetFields();
    setSelectedModules([]);
    setModuleSelectorVisible(false);
  };

  const handleEditClick = (record: any) => {
    if (!canUpdate) {
      toast.warning("You don't have permission to edit permissions.");
      return;
    }
    setIsEditMode(true);
    setCurrentEditingPermissionId(record.permission_id);
    form.setFieldsValue({
      model_id: [record.model_id],
      role_name: record.role_name,
      employee_id: record.employee_id || "",
      allow_all: record.allow_all,
      read: record.read,
      write: record.write,
      create: record.create,
      delete: record.delete,
      export: record.export,
    });
    setSelectedModules([record.model_id]);
    setModalVisible(true);
  };

  const handleDeleteClick = (permissionId: number) => {
    if (!canDelete) {
      toast.warning("You don't have permission to delete permissions.");
      return;
    }
    setDeletePermissionId(permissionId);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (deletePermissionId === null) return;
    try {
      await deletePermission(deletePermissionId, empToken);
      const updated = await getAllPermissions(empToken);
      setPermissions(updated);
    } catch (error) {
      console.error("Failed to delete permission");
    } finally {
      setDeleteModalVisible(false);
      setDeletePermissionId(null);
    }
  };

  const handleAddClick = () => {
    if (!canCreate) {
      toast.warning("You don't have permission to create permissions.");
      return;
    }
    form.resetFields();
    setIsEditMode(false);
    setCurrentEditingPermissionId(null);
    setSelectedModules([]);
    setModalVisible(true);
  };

  // 🔹 Filter employees by selected role from form
  const currentRoleValue = Form.useWatch("role_name", form);
  const filteredEmployees = useMemo(() => {
    if (!currentRoleValue) return employees || [];
    return (employees || []).filter(
      (emp) => emp.role_name === currentRoleValue
    );
  }, [employees, currentRoleValue]);

  const columns = [
    {
      title: "Module",
      dataIndex: ["module", "name"],
      key: "module_name",
    },
    {
      title: "Role",
      dataIndex: "role_name",
    },
    {
      title: "Employee",
      key: "employee_id",
      render: (_: any, record: any) => {
        const emp = employeeNameMap[record.employee_id];
        if (!emp) return <span>-</span>;
        return (
          <div>
            <div>
              <strong>{emp.name}</strong>
            </div>
            <span>{emp.department}</span>
          </div>
        );
      },
    },
    {
      title: "All",
      dataIndex: "allow_all",
      render: (value: boolean) => (
        <div className={value ? "checkbox-primary-checked" : ""}>
          <Checkbox checked={value} />
        </div>
      ),
    },
    {
      title: "Read",
      dataIndex: "read",
      render: (value: boolean) => <Checkbox checked={value} />,
    },
    {
      title: "Write",
      dataIndex: "write",
      render: (value: boolean) => <Checkbox checked={value} />,
    },
    {
      title: "Create",
      dataIndex: "create",
      render: (value: boolean) => <Checkbox checked={value} />,
    },
    {
      title: "Delete",
      dataIndex: "delete",
      render: (value: boolean) => <Checkbox checked={value} />,
    },
    {
      title: "Export",
      dataIndex: "export",
      render: (value: boolean) => <Checkbox checked={value} />,
    },
    {
      title: "Actions",
      key: "action",
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditClick(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
              size="small"
              onClick={() => handleDeleteClick(record.permission_id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div>
            <h2 className="mb-1">Permissions</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                {/* <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li> */}
                <li className="breadcrumb-item active">
                  <Link to={all_routes.rolePermission}>
                    {" "}
                    <i className="ti ti-arrow-left"/>
                    Back
                  </Link>
                </li>
                <li className="breadcrumb-item active">Permission</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center ms-auto gap-2">
            {/* 🔹 New Info Button for Instructions */}
            <div className="mb-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setInstructionsVisible(true)}
              >
                <InfoCircleOutlined className="me-1" />
                Instructions
              </button>
            </div>
            <div className="mb-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddClick}
              >
                <i className="ti ti-circle-plus me-2" />
                Add Permission
              </button>
            </div>
            <div>
              <CollapseHeader />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Permissions Table</h5>
            <div className="d-flex gap-2 align-items-center">
              <select
                className="form-select form-select-sm"
                style={{ width: "150px" }}
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
              >
                <option value="">All Modules</option>
                {Array.isArray(modules) &&
                  modules.map((module) => (
                    <option key={module.model_id} value={module.name}>
                      {module.name}
                    </option>
                  ))}
              </select>
              <select
                className="form-select form-select-sm"
                style={{ width: "150px" }}
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="">All Roles</option>
                {Array.isArray(roles) &&
                  roles.map((role: any) => (
                    <option key={role.role_name} value={role.role_name}>
                      {role.role_name}
                    </option>
                  ))}
              </select>
              <select
                className="form-select form-select-sm"
                style={{ width: "120px" }}
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="Default">Default</option>
                <option value="Custom">Custom</option>
              </select>
              <div className="input-group" style={{ width: "250px" }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search permissions..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <span className="input-group-text">
                  <i className="ti ti-search"></i>
                </span>
              </div>
            </div>
          </div>

          <div className="card-body p-0">
            <Table
              columns={columns}
              dataSource={filteredPermissions}
              loading={loading}
              rowKey="permission_id"
            />
          </div>
        </div>
      </div>
      {/* 🔹 Instructions Modal (Refactored with All Content, No Tables) */}
      <div
        className={`modal fade ${instructionsVisible ? "show" : ""}`}
        style={{
          display: instructionsVisible ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex={-1}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content shadow-lg border-0 rounded-3">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title">Permission Matrix Instructions</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setInstructionsVisible(false)}
              ></button>
            </div>

            {/* Body */}
            <div className="modal-body">
              {/* General Permission Types */}
              <div className="alert alert-info mb-3">
                <h6 className="alert-heading mb-2">General Permission Types</h6>
                <ul className="mb-0 ps-3">
                  <li>
                    <strong>Allow All</strong> – User can perform all actions
                    for that module.
                  </li>
                  <li>
                    <strong>Read</strong> – View module data.
                  </li>
                  <li>
                    <strong>Create</strong> – Add or create new data in the
                    module.
                  </li>
                  <li>
                    <strong>Write</strong> – Update existing data.
                  </li>
                  <li>
                    <strong>Delete</strong> – Remove data from the module.
                  </li>
                  <li>
                    <strong>Export</strong> – Export or download module data.
                  </li>
                </ul>
                <p className="small text-muted mt-2">
                  User must have <strong>Read</strong> or{" "}
                  <strong>Allow All</strong> permission to see the sidebar
                  module.
                </p>
              </div>

              {/* TMS Modules Special Case */}
              <div className="alert alert-info mb-3">
                <h6 className="alert-heading mb-2">
                  Special Case: TMS Modules (Tickets & Tasks)
                </h6>
                <ul className="mb-0 ps-3">
                  <li>
                    <strong>Allow All</strong> – View and manage all data (like
                    an Admin).
                  </li>
                  <li>
                    <strong>Read</strong> – View only own assigned or created
                    tickets/tasks.
                  </li>
                  <li>
                    <strong>Read + Write + Create</strong> – Full control but
                    only for the user’s own data.
                  </li>
                </ul>
              </div>

              {/* Summary */}
              <div className="alert alert-info mb-0">
                <h6 className="text-dark mb-2">Summary</h6>
                <p className="text-muted mb-0">
                  TMS modules filter data based on{" "}
                  <strong>access levels</strong>. All other modules follow the
                  standard meaning of <strong>Read</strong>,{" "}
                  <strong>Create</strong>, <strong>Write</strong>,{" "}
                  <strong>Delete</strong>, and <strong>Export</strong>.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setInstructionsVisible(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {instructionsVisible && (
        <div
          className="modal-backdrop fade show"
          onClick={() => setInstructionsVisible(false)}
        ></div>
      )}

      <div
        className={`modal fade ${
          modalVisible && !moduleSelectorVisible ? "show" : ""
        }`}
        style={{
          display: modalVisible && !moduleSelectorVisible ? "block" : "none",
        }}
        tabIndex={-1}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {isEditMode ? "Edit Permission" : "Add Permission"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
              ></button>
            </div>
            <div className="modal-body">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleAddPermission}
                initialValues={{
                  allow_all: false,
                  read: false,
                  write: false,
                  create: false,
                  delete: false,
                  export: false,
                }}
              >
                <div className="row">
                  <div className="col-24">
                    <div className="mb-3">
                      <label className="form-label">
                        Modules <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        value={selectedModules
                          .map(
                            (id) =>
                              modules.find((mod) => mod.model_id === id)?.name
                          )
                          .filter(Boolean)
                          .join(", ")}
                        placeholder="Click to select modules"
                        rows={2}
                        readOnly
                        onClick={() => setModuleSelectorVisible(true)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: "#fafafa",
                          borderColor: "#d9d9d9",
                        }}
                      />
                      <Form.Item
                        name="model_id"
                        style={{ display: "none" }}
                        rules={[
                          {
                            required: true,
                            message: "Please select at least one module",
                          },
                        ]}
                      >
                        <Select mode="multiple" style={{ display: "none" }} />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="col-24">
                    <div className="mb-3">
                      <label className="form-label">
                        Role <span className="text-danger">*</span>
                      </label>
                      <Form.Item
                        name="role_name"
                        style={{ marginBottom: 0 }}
                        rules={[
                          { required: true, message: "Please select a role" },
                        ]}
                      >
                        <Select
                          style={{ width: "100%" }}
                          getPopupContainer={(trigger) => trigger.parentNode}
                        >
                          <Option value="">Select a role</Option>
                          {Array.isArray(roles) &&
                            roles.map((role: any) => (
                              <Option
                                key={role.role_name}
                                value={role.role_name}
                              >
                                {role.role_name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="col-24">
                    <div className="mb-3">
                      <label className="form-label">Employee</label>
                      <Form.Item name="employee_id" style={{ marginBottom: 0 }}>
                        <Select
                          showSearch
                          allowClear
                          optionFilterProp="children"
                          style={{ width: "100%" }}
                          getPopupContainer={(trigger) => trigger.parentNode}
                        >
                          <Option value="">Select an employee</Option>
                          {filteredEmployees.map((emp) => (
                            <Option
                              key={emp.employee_id}
                              value={emp.employee_id}
                            >
                              {`${emp.firstname} ${emp.lastname} (${emp.department_name}) - ${emp.employee_id}`}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="col-4">
                    <Form.Item
                      name="allow_all"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox>Allow All</Checkbox>
                    </Form.Item>
                  </div>
                  <div className="col-4">
                    <Form.Item
                      name="read"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox>Read</Checkbox>
                    </Form.Item>
                  </div>
                  <div className="col-4">
                    <Form.Item
                      name="write"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox>Write</Checkbox>
                    </Form.Item>
                  </div>
                  <div className="col-4">
                    <Form.Item
                      name="create"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox>Create</Checkbox>
                    </Form.Item>
                  </div>
                  <div className="col-4">
                    <Form.Item
                      name="delete"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox>Delete</Checkbox>
                    </Form.Item>
                  </div>
                  <div className="col-4">
                    <Form.Item
                      name="export"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox>Export</Checkbox>
                    </Form.Item>
                  </div>
                </div>
              </Form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-cancel me-2"
                onClick={handleCancelForm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => form.submit()}
              >
                {isEditMode ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`modal fade ${moduleSelectorVisible ? "show" : ""}`}
        style={{ display: moduleSelectorVisible ? "block" : "none" }}
        tabIndex={-1}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Select Modules</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setModuleSelectorVisible(false)}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="selectAll"
                    checked={
                      selectedModules.length === modules.length &&
                      modules.length > 0
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedModules(modules.map((mod) => mod.model_id));
                      } else {
                        setSelectedModules([]);
                      }
                    }}
                  />
                  <label
                    className="form-check-label fw-bold"
                    htmlFor="selectAll"
                  >
                    Select All
                  </label>
                </div>
              </div>
              <hr />
              <div className="row">
                {Array.isArray(modules) &&
                  modules.map((mod) => (
                    <div className="col-6" key={mod.model_id}>
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`module-${mod.model_id}`}
                          checked={selectedModules.includes(mod.model_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedModules([
                                ...selectedModules,
                                mod.model_id,
                              ]);
                            } else {
                              setSelectedModules(
                                selectedModules.filter(
                                  (id) => id !== mod.model_id
                                )
                              );
                            }
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`module-${mod.model_id}`}
                        >
                          {mod.name}
                        </label>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  form.setFieldValue("model_id", selectedModules);
                  setModuleSelectorVisible(false);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
      <Modal
        title="Confirm Deletion"
        open={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this permission?</p>
      </Modal>
      {(modalVisible || moduleSelectorVisible) && (
        <div
          className="modal-backdrop fade show"
          onClick={
            moduleSelectorVisible
              ? () => setModuleSelectorVisible(false)
              : handleCloseModal
          }
        ></div>
      )}
    </div>
  );
};
export default PermissionPage;
